<template>
  <main class="admin-shell">
    <!-- Per-page SEO: unique title + meta description. -->
    <SeoHead :seo="seo" />

    <section class="admin-shell__section" aria-labelledby="admin-shell-heading">
      <v-container class="admin-shell__container" fluid>
        <v-row>
          <!--
            Persistent admin navigation region (Requirement 10.3).

            Driven by the `adminNavItems` array so future admin screens are
            added by appending an entry here and registering a child route —
            no Auth_Guard change is required.
          -->
          <v-col cols="12" md="3" lg="2">
            <nav class="admin-shell__nav" aria-label="Admin navigation">
              <v-list class="admin-shell__nav-list" nav density="compact">
                <!--
                  Internal entries use `to` (router link); external ones use
                  `href` and open in a new tab. `rel="noopener noreferrer"` is
                  set on external links so the opened page cannot reach back
                  through `window.opener`. The trailing icon and the aria-label
                  suffix both signal that the link leaves the application —
                  the icon for sighted users, the label for screen readers.
                -->
                <v-list-item
                  v-for="item in visibleNavItems"
                  :key="item.title"
                  :to="item.to"
                  :href="item.href"
                  :target="item.external ? '_blank' : undefined"
                  :rel="item.external ? 'noopener noreferrer' : undefined"
                  :prepend-icon="item.icon"
                  :append-icon="item.external ? 'mdi-open-in-new' : undefined"
                  :title="item.title"
                  :aria-label="item.external ? `${item.title} (opens in a new tab)` : undefined"
                  :exact="item.exact === true"
                  :data-test="item.external ? 'nav-external' : undefined"
                />
              </v-list>
            </nav>
          </v-col>

          <!-- Main admin content column. -->
          <v-col cols="12" md="9" lg="10">
            <div class="admin-shell__header">
              <h1 id="admin-shell-heading" class="admin-shell__heading">
                {{ content.heading }}
              </h1>

              <!--
                Logout control — visible and enabled only while authenticated
                (Requirement 8.1). Removed once the session is cleared
                (Requirement 8.4).
              -->
              <v-btn
                v-if="isAuthenticated"
                class="admin-shell__logout text-none"
                color="primary"
                variant="flat"
                :loading="loggingOut"
                @click="onLogout"
              >
                {{ content.logoutLabel }}
              </v-btn>
            </div>

            <!--
              Logout-failure indication (Requirement 8.5): shown when clearing
              the session did not complete. The existing session is preserved.
            -->
            <v-alert
              v-if="logoutError"
              type="error"
              variant="tonal"
              class="mb-4"
              role="alert"
            >
              {{ content.logoutErrorText }}
            </v-alert>

            <!--
              Shown when the Auth_Guard redirected here from an admin-only screen
              because this account is not an administrator. Without it the
              redirect would look like a broken link.
            -->
            <v-alert
              v-if="wasDenied"
              type="info"
              variant="tonal"
              class="mb-4"
              role="status"
              data-test="denied-notice"
            >
              {{ content.deniedText }}
            </v-alert>

            <!--
              Profile display (Requirement 10.2). When a token is present but no
              profile fields were persisted, render the layout without the
              profile-dependent content and surface a clear indication that the
              profile information is unavailable (Requirement 10.5).
            -->
            <v-card class="admin-shell__profile" variant="flat">
              <v-card-item>
                <template v-if="hasProfile">
                  <!--
                    The role chip is a SIBLING of the heading, not a child of it:
                    keeping it out of the <h2> means the accessible heading text
                    stays just the person's name rather than "Ada Lovelace
                    Administrator".
                  -->
                  <div class="admin-shell__profile-identity">
                    <h2 class="admin-shell__profile-name">{{ displayName }}</h2>
                    <!-- Makes the signed-in account's role visible, so it is
                         clear why the Users entry is or is not offered. -->
                    <v-chip
                      :color="roleChipColor"
                      size="small"
                      variant="flat"
                      data-test="role-chip"
                    >
                      {{ roleLabel }}
                    </v-chip>
                  </div>
                  <p class="admin-shell__profile-email">{{ profile.email }}</p>
                </template>
                <v-alert
                  v-else
                  type="info"
                  variant="tonal"
                  role="status"
                >
                  {{ content.profileUnavailableText }}
                </v-alert>
              </v-card-item>
            </v-card>

            <!--
              Nested mount point (Requirement 10.3): future admin screens plug
              in here as child routes of the Admin_Shell.
            -->
            <div class="admin-shell__content">
              <router-view />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </main>
</template>

<script>
// AdminShellPage — the authenticated Admin_Shell landing (Requirement 10).
//
// Renders inside the existing `App.vue` shell using the same Vuetify + brand
// theme/layout conventions as the public marketing site (Requirement 10.4).
// It displays the persisted non-sensitive profile fields (Requirement 10.2),
// renders a persistent, extensible admin navigation region plus a nested
// <router-view> (Requirement 10.3), shows a logout control while authenticated
// (Requirement 8.1), and falls back to a "profile information is unavailable"
// indication when a token is present but no profile was persisted
// (Requirement 10.5).
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset.
import { mapStores } from 'pinia'
import SeoHead from '@/components/SeoHead.vue'
import { useAuthStore } from '@/stores/auth'
import { runtimeConfig } from '@/config/runtimeConfig'
import {
  ROLE_EMPLOYEE,
  ROLE_ADMIN,
  hasAtLeast,
  labelForRole,
} from '@/services/roles'

// Persistent admin navigation entries (Requirement 10.3). Future admin screens
// are added by appending an entry here and registering the matching child
// route — the Auth_Guard needs no change.
//
// `exact` controls active highlighting. The Dashboard entry points at the parent
// `/admin` route, which every child path also matches, so it must be exact or it
// would stay highlighted on every admin screen. Section entries are NOT exact,
// so "Projects" stays highlighted while you are in the project editor and
// "Users" stays highlighted while you are in the user editor.
//
// `minRole` is the minimum privilege rank required to SEE an entry. Expressed as
// a minimum rather than an exact role so a super administrator is never excluded
// from an administrator entry. Entries a user cannot use are hidden rather than
// shown-and-disabled, so the nav reflects what is actually available.
//
// Presentation only — the route guard and the server-side `isAdmin` /
// `isSuperAdmin` policies are what actually enforce access. A superadmin-only
// screen added later needs `minRole: ROLE_SUPERADMIN` here plus
// `meta.requiresSuperAdmin` on its route.
//
// An entry is either internal (`to`, a router destination) or external (`href`
// plus `external: true`, rendered as an anchor opening in a new tab).
const adminNavItems = [
  {
    title: 'Dashboard',
    icon: 'mdi-view-dashboard',
    to: { name: 'admin' },
    exact: true,
    minRole: ROLE_EMPLOYEE,
  },
  {
    title: 'Projects',
    icon: 'mdi-folder-multiple-image',
    to: { name: 'admin-projects' },
    // Admin-only: the whole project-management area (and its API) is isAdmin-gated,
    // so an employee could neither list nor edit projects there. Don't advertise it.
    minRole: ROLE_ADMIN,
  },
  {
    title: 'Users',
    icon: 'mdi-account-multiple',
    to: { name: 'admin-users' },
    minRole: ROLE_ADMIN,
  },
  // Survey documentation. An EXTERNAL destination, so it carries `href` instead
  // of `to` and is marked `external` — the nav renders it as an anchor opening in
  // a new tab rather than a router link. Available to every signed-in user,
  // since the documentation is for all staff.
  //
  // The URL comes from runtimeConfig so it can be repointed without a code
  // change (see the note there about serving the docs from `/docs` later).
  {
    title: 'Survey docs',
    icon: 'mdi-book-open-variant',
    href: runtimeConfig.links.docs,
    external: true,
    minRole: ROLE_EMPLOYEE,
  },
  // Forms. Any signed-in employee can fill forms (e.g. the Vehicle Weekly
  // Prestart); admins additionally get submission views within the area.
  {
    title: 'Forms',
    icon: 'mdi-clipboard-text-outline',
    to: { name: 'forms' },
    minRole: ROLE_EMPLOYEE,
  },
]

// Co-located copy for the shell. Kept inline because the Admin_Shell is a
// single-purpose authenticated utility screen.
const content = {
  heading: 'Admin',
  logoutLabel: 'Log out',
  logoutErrorText: 'Logout did not complete. Please try again.',
  profileUnavailableText: 'Profile information is unavailable.',
  deniedText: 'That area is restricted to administrators.',
}

const seo = {
  title: 'Admin | ThinkSpatial',
  description: 'ThinkSpatial administration area.',
}

export default {
  name: 'AdminShellPage',
  components: {
    SeoHead,
  },
  data () {
    return {
      adminNavItems,
      content,
      seo,
      // True while a logout is in flight, to prevent duplicate activations.
      loggingOut: false,
      // Set when clearing the session did not complete (Requirement 8.5).
      logoutError: false,
    }
  },
  computed: {
    // Pinia Options-API binding → exposes `this.authStore`.
    ...mapStores(useAuthStore),
    // Derived authentication state (drives logout-control visibility).
    isAuthenticated () {
      return this.authStore.isAuthenticated
    },
    // The persisted, display-safe profile subset (or null when logged out).
    profile () {
      return this.authStore.profile
    },
    // Whether the signed-in user holds administrator access (admin OR
    // superadmin). Drives which nav entries are offered; the route guard and the
    // server enforce the real rule.
    isAdmin () {
      return this.authStore.isAdmin
    },
    // The signed-in user's role, used for rank comparisons below.
    role () {
      return this.authStore.role
    },
    // The nav entries this user's rank permits. Entries above their rank are
    // hidden rather than shown-and-disabled, so the nav never advertises a screen
    // that would redirect them straight back here.
    visibleNavItems () {
      return this.adminNavItems.filter((item) =>
        hasAtLeast(this.role, item.minRole || ROLE_EMPLOYEE),
      )
    },
    // Set when the Auth_Guard redirected here because the requested screen
    // requires the administrator role (it appends `?denied=<route>`).
    wasDenied () {
      const query = (this.$route && this.$route.query) || {}
      return typeof query.denied === 'string' && query.denied !== ''
    },
    // Role label shown alongside the profile so it is obvious which kind of
    // account is signed in.
    roleLabel () {
      return labelForRole(this.role)
    },
    // Colour for the role chip, so the three tiers are visually distinct.
    roleChipColor () {
      if (this.authStore.isSuperAdmin) return '#41281b'
      return this.isAdmin ? 'primary' : 'grey'
    },
    // A token is present but no usable profile fields were persisted → show the
    // "profile information is unavailable" fallback (Requirement 10.5).
    hasProfile () {
      const p = this.profile
      if (!p || typeof p !== 'object') {
        return false
      }
      // Consider the profile usable when it carries any display field.
      return Boolean(p.email || p.first_name || p.last_name)
    },
    // Non-sensitive display name assembled from the persisted profile fields
    // (Requirement 10.2). Falls back to the email when no name is present.
    displayName () {
      const p = this.profile || {}
      const name = [p.first_name, p.last_name]
        .filter((part) => typeof part === 'string' && part.trim() !== '')
        .join(' ')
        .trim()
      return name || p.email || ''
    },
  },
  methods: {
    // Logout handler (Requirements 8.2, 8.4, 8.5).
    //
    // Idempotently clears the Session_Store, then routes to the Login page.
    // After `clear()`, the Api_Client request interceptor no longer finds a
    // token, so subsequent requests omit the Authorization header
    // (Requirement 8.3).
    //
    // If clearing the session fails, the administrator is kept on the current
    // view, a "logout did not complete" indication is shown, and the existing
    // session data is preserved with no partial removal (Requirement 8.5).
    async onLogout () {
      if (this.loggingOut) {
        return
      }
      this.loggingOut = true
      this.logoutError = false

      try {
        this.authStore.clear()
      } catch (e) {
        // Removal failed: keep the user in place with the session intact.
        this.logoutError = true
        this.loggingOut = false
        return
      }

      try {
        await this.$router.push({ name: 'login' })
      } catch (e) {
        // Navigation failed after the session was cleared; surface the failure
        // without leaving the administrator stranded silently.
        this.logoutError = true
      } finally {
        this.loggingOut = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-shell__section {
  padding: 48px 0;
  background-color: #f7f5f4;
  min-height: 70vh;
}

.admin-shell__container {
  width: 100%;
}

.admin-shell__nav {
  background-color: #fff;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 10px 30px rgba(65, 40, 27, 0.08);
}

.admin-shell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.admin-shell__heading {
  font-size: 2rem;
  font-weight: 800;
  color: #41281b;
}

.admin-shell__logout {
  font-weight: 700;
  letter-spacing: 0.02em;
}

.admin-shell__profile {
  background-color: #fff;
  border-radius: 12px;
  margin-bottom: 24px;
}

.admin-shell__profile-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.admin-shell__profile-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #41281b;
}

.admin-shell__profile-email {
  font-size: 1rem;
  color: #4a4a4a;
}

.admin-shell__content {
  width: 100%;
}
</style>
